/**
 * Public, self-contained PDF page viewer for snapshots. Renders a non-US drug's
 * actual document pages (public GCS image URLs stored in the snapshot) with a
 * TOC and click-to-zoom — no auth, no viewer API. One column per drug.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ExportDrugPdf, ExportTocEntry } from '../utils/compareExport';
import type { TOCEntry } from '../types/viewer';
import TOCSidebar from './viewer/TOCSidebar';
import PageZoomLightbox from './viewer/PageZoomLightbox';

type SnapMark = NonNullable<ExportDrugPdf['marks']>[number];

/** A snapshot TOC carries the live-viewer hierarchy when it was exported after
 *  the unification (id + level present). Older snapshots have only the flat
 *  ``{section_number,title,page_num}`` shape → fall back to the flat list. */
function isHierarchical(toc: ExportTocEntry[]): boolean {
  return toc.some((t) => typeof t.id === 'number' && typeof t.level === 'number');
}

/** Adapt a snapshot TOC row to the live ``TOCEntry`` the shared TOCSidebar
 *  consumes (filling the few fields a snapshot doesn't carry with safe
 *  defaults), so the snapshot and the live viewer share ONE TOC renderer. */
function toTOCEntries(toc: ExportTocEntry[]): TOCEntry[] {
  return toc.map((t, i) => ({
    id: t.id ?? i,
    section_number: t.section_number,
    title: t.title,
    level: t.level ?? 1,
    parent_section_number: t.parent_section_number ?? null,
    parent_section_id: t.parent_section_id ?? null,
    page_start: t.page_start ?? t.page_num ?? null,
    page_end: t.page_end ?? null,
    section_type: t.section_type ?? null,
    group_key: t.group_key ?? null,
    group_label: t.group_label ?? null,
    is_group_header: t.is_group_header ?? false,
  }));
}

/** Keyword highlight boxes for one snapshot page (colored rects blended over
 *  the page image). Shared by the inline page and the zoom lightbox so both
 *  highlight identically. */
function SnapMarks({ marks }: { marks: SnapMark[] }) {
  return (
    <>
      {marks.map((m, j) => (
        <div
          key={j}
          className="pointer-events-none absolute mix-blend-multiply"
          style={{
            left: `${m.x0 * 100}%`,
            top: `${m.y0 * 100}%`,
            width: `${Math.max(0, m.x1 - m.x0) * 100}%`,
            height: `${Math.max(0, m.y1 - m.y0) * 100}%`,
            backgroundColor: m.color,
            opacity: 0.55,
          }}
        />
      ))}
    </>
  );
}

export default function SnapshotPdfViewer({
  name,
  subtitle,
  pdf,
}: {
  name: string;
  subtitle?: string | undefined;
  pdf: ExportDrugPdf;
}) {
  const [tocOpen, setTocOpen] = useState(false);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const pages = pdf.pages;
  const tocWithPages = pdf.toc.filter((t) => t.title);
  // Hierarchical Contents (same renderer as the live TOCSidebar) when the
  // snapshot carries the tree; older flat snapshots fall back to the flat list.
  const hierarchical = useMemo(() => isHierarchical(tocWithPages), [tocWithPages]);
  const tocEntries = useMemo(() => toTOCEntries(tocWithPages), [tocWithPages]);

  const scrollToPage = (pageNum: number, smooth = true) => {
    const el = scrollRef.current?.querySelector(`#snap-pg-${pageNum}`);
    el?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    setTocOpen(false);
  };

  // Track the topmost visible page so the shared TOCSidebar can highlight the
  // active section as the user scrolls (mirrors the live viewer behaviour).
  const onScroll = () => {
    const root = scrollRef.current;
    if (!root) return;
    const top = root.getBoundingClientRect().top;
    let best = 0;
    for (const p of pages) {
      const el = root.querySelector(`#snap-pg-${p.page_num}`);
      if (!el) continue;
      if ((el as HTMLElement).getBoundingClientRect().top - top <= 8) best = p.page_num;
      else break;
    }
    if (best && best !== currentPage) setCurrentPage(best);
  };

  // On first load, scroll the PDF column to the matched section so the snapshot
  // lands on the evidence instead of the cover page:
  //   • keyword snapshots → the page of the first highlight (``marks``)
  //   • plain snapshots (no keyword) → the first real content section's page
  // Once-only; lazy-loaded page images need a tick to lay out before scroll.
  const didScroll = useRef(false);
  useEffect(() => {
    if (didScroll.current) return;
    const markPage = (pdf.marks ?? [])[0]?.page;
    const firstSecPage = tocEntries.find((t) => (t.page_start ?? 0) > 0)?.page_start ?? null;
    const target = markPage || firstSecPage;
    if (!target) return;
    didScroll.current = true;
    const t = setTimeout(() => scrollToPage(target, false), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf.marks, tocEntries]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-800">{name}</div>
          {subtitle && <div className="truncate text-xs text-slate-500">{subtitle}</div>}
        </div>
        {tocWithPages.length > 0 && (
          <button
            onClick={() => setTocOpen((o) => !o)}
            className="shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Contents
          </button>
        )}
        <span className="shrink-0 text-[11px] text-slate-400">{pages.length} pages</span>
      </header>

      <div className="relative min-h-0 flex-1">
        {tocOpen && (
          <div className="absolute right-2 top-2 z-20 max-h-[80%] w-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
            {hierarchical ? (
              // Shared renderer — identical indented, collapsible tree as the
              // live document viewer (parent/child, group banners, p.N markers).
              <TOCSidebar
                entries={tocEntries}
                currentPage={currentPage}
                onNavigate={(e) => {
                  if (e.page_start !== null) scrollToPage(e.page_start);
                }}
              />
            ) : (
              <div className="p-1">
                <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Contents</div>
                {tocWithPages.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => t.page_num && scrollToPage(t.page_num)}
                    disabled={!t.page_num}
                    className="flex w-full items-baseline gap-2 rounded px-2 py-1 text-left text-xs hover:bg-slate-50 disabled:cursor-default"
                  >
                    {t.section_number && <span className="shrink-0 font-medium text-indigo-600">{t.section_number}</span>}
                    <span className="truncate text-slate-600">{t.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div ref={scrollRef} onScroll={onScroll} className="h-full overflow-y-auto bg-slate-100 p-2">
          {pages.map((p, i) => {
            const pageMarks = (pdf.marks ?? []).filter((m) => m.page === p.page_num);
            return (
              <div key={p.page_num} id={`snap-pg-${p.page_num}`} className="mb-2">
                <div className="relative">
                  <img
                    src={p.image_url}
                    loading="lazy"
                    alt={`Page ${p.page_num}`}
                    onClick={() => setZoomIdx(i)}
                    className="w-full cursor-zoom-in rounded bg-white shadow-sm ring-1 ring-slate-200"
                  />
                  {/* Keyword highlight overlay (colored boxes blended over the page image). */}
                  <SnapMarks marks={pageMarks} />
                </div>
                <div className="py-0.5 text-center text-[10px] text-slate-400">Page {p.page_num}</div>
              </div>
            );
          })}
          {pages.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No pages.</div>}
        </div>
      </div>

      {zoomIdx !== null && pages[zoomIdx] && (
        <PageZoomLightbox
          pageCount={pages.length}
          index={zoomIdx}
          onIndex={setZoomIdx}
          onClose={() => setZoomIdx(null)}
          pageLabel={(i) => `Page ${pages[i]!.page_num}`}
          renderPage={(i) => {
            const page = pages[i]!;
            const marks = (pdf.marks ?? []).filter((m) => m.page === page.page_num);
            return (
              <div className="relative w-full">
                <img src={page.image_url} alt={`Page ${page.page_num}`} className="block w-full bg-white shadow-2xl" />
                <SnapMarks marks={marks} />
              </div>
            );
          }}
        />
      )}
    </div>
  );
}
